import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import Icon from "../Icon/Icon";
import "./Drawer.css";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** grey text under the title */
  description?: ReactNode;
  /** buttons at the bottom; they stay visible while the body scrolls */
  footer?: ReactNode;
  /** md = 480px (default), lg = 640px */
  size?: "md" | "lg";
  /** "row" = buttons side by side (default); "stack" = one full-width
      button per line, for long labels (put the main action first) */
  footerLayout?: "row" | "stack";
  /** label for the X button, for screen readers */
  closeLabel?: string;
  children?: ReactNode;
}

/**
 * Side panel from the right for lists and longer tasks (Manage properties,
 * Manage tenants). Closes on Escape, the X and a click outside.
 */
const Drawer = ({
  open,
  onClose,
  title,
  description,
  footer,
  size = "md",
  footerLayout = "row",
  closeLabel = "Close",
  children,
}: DrawerProps) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  // latest onClose without re-running the effect on every render
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      <div className="ds-drawer-backdrop" onMouseDown={onClose} />
      <div
        ref={panelRef}
        className={`ds-drawer ds-drawer--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        <div className="ds-drawer__head">
          <div>
            {title && (
              <h2 id={titleId} className="ds-drawer__title">
                {title}
              </h2>
            )}
            {description && <p className="ds-drawer__text">{description}</p>}
          </div>
          <button
            type="button"
            className="ds-drawer__close"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <div className="ds-drawer__body">{children}</div>
        {footer && (
          <div
            className={`ds-drawer__footer ds-drawer__footer--${footerLayout}`}
          >
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default Drawer;
